import React, { Component, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';

import { withFirebase } from '../Firebase';
import { SignInGoogle } from '../SignIn';
import * as ROUTES from '../../constants/routes';
import { buildFullName, getNameFromEmail } from '../../utils/userName';

import '../Auth/styles.css';

const SignUpPage = () => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="auth-page">
      <div className="auth-hero">
        {/* Decorative background elements */}
        <div className="hero-motif">
          <div className="hero-motif-gradient-1" />
          <div className="hero-motif-gradient-2" />
          <div className="hero-motif-gradient-3" />
          <span className="hero-motif-line" />
          <div className="hero-motif-orb" />
        </div>
        
        <div className="auth-hero-content">
          <span className="auth-overline">Douglas &amp; McCartney</span>
          <h1 className="auth-title headline-reveal">Begin with better.</h1>
          <p className="auth-subtitle">Create your Douglas &amp; McCartney account.</p>
        </div>
      </div>

      <div className="auth-form-panel">
        <div className="auth-form-card">
          <header className="auth-form-header">
            <span className="auth-brand-wordmark">Douglas &amp; McCartney</span>
            <h2>Create account</h2>
            <p>It takes less than a minute to join Douglas &amp; McCartney.</p>
          </header>

          <div className="auth-oauth-section">
            <SignInGoogle />
            <div className="auth-separator">
              <span>or</span>
            </div>
          </div>

          <SignUpForm />

          <div style={{ borderTop: '1px solid rgba(168, 168, 168, 0.2)', paddingTop: '2rem' }}>
            <p className="auth-inline-link">
              Already have an account?{' '}
              <Link to={ROUTES.SIGN_IN} className="auth-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const INITIAL_STATE = {
  firstName: '',
  lastName: '',
  email: '',
  passwordOne: '',
  passwordTwo: '',
  phoneNumber: '',
  company: '',
  role: '',
  termsAccepted: false,
  isSubmitting: false,
  error: null,
};

const ERROR_CODE_ACCOUNT_EXISTS = 'auth/email-already-in-use';

const ERROR_MSG_ACCOUNT_EXISTS = `
  An account with this E-Mail address already exists.
  Try to login with this account instead. If you think the
  account is already used from one of the social logins, try
  to sign in with one of them. Afterward, associate your accounts
  on your personal account page.
`;

class SignUpFormBase extends Component {
  constructor(props) {
    super(props);

    this.state = { ...INITIAL_STATE };
  }

  onSubmit = event => {
    event.preventDefault();

    const { firstName, lastName, email, passwordOne, phoneNumber, company, role, isSubmitting } = this.state;
    if (isSubmitting) {
      return;
    }

    const fullName = buildFullName(firstName, lastName) || getNameFromEmail(email);
    this.setState({ isSubmitting: true, error: null });

    this.props.firebase
      .doCreateUserWithEmailAndPassword(email, passwordOne)
      .then(authUser => {
        // Create a user in your Firebase Firestore database
        return this.props.firebase.user(authUser.user.uid).set(
          {
            firstName,
            lastName,
            fullName,
            username: fullName,
            signupMethod: 'manual',
            email,
            phoneNumber,
            company,
            role,
            createdAt: this.props.firebase.fieldValue.serverTimestamp(),
          },
          { merge: true },
        );
      })
      .then(() => {
        return this.props.firebase.doSendEmailVerification();
      })
      .then(() => {
        this.setState({ ...INITIAL_STATE });
        this.props.history.push(ROUTES.EMAIL_VERIFICATION);
      })
      .catch(error => {
        if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
          error.message = ERROR_MSG_ACCOUNT_EXISTS;
        }

        this.setState({ error, isSubmitting: false });
      });
  };

  onChange = event => {
    this.setState({ [event.target.name]: event.target.value });
  };

  onToggleTerms = event => {
    this.setState({ termsAccepted: event.target.checked });
  };

  render() {
    const {
      firstName,
      lastName,
      email,
      passwordOne,
      passwordTwo,
      phoneNumber,
      company,
      role,
      termsAccepted,
      isSubmitting,
      error,
    } = this.state;

    const isInvalid =
      passwordOne !== passwordTwo ||
      passwordOne === '' ||
      email === '' ||
      firstName === '' ||
      lastName === '' ||
      !termsAccepted;

    return (
      <form onSubmit={this.onSubmit} className="auth-form" noValidate>
        <div className="auth-field-row">
          <div className="auth-field">
            <label htmlFor="signup-first-name">First Name</label>
            <input
              id="signup-first-name"
              name="firstName"
              value={firstName}
              onChange={this.onChange}
              type="text"
              placeholder="First name"
              required
            />
          </div>
          <div className="auth-field">
            <label htmlFor="signup-last-name">Last Name</label>
            <input
              id="signup-last-name"
              name="lastName"
              value={lastName}
              onChange={this.onChange}
              type="text"
              placeholder="Last name"
              required
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="signup-email">Email</label>
          <input
            id="signup-email"
            name="email"
            value={email}
            onChange={this.onChange}
            type="email"
            placeholder="name@example.com"
            required
          />
        </div>

        <div className="auth-field-row">
          <div className="auth-field">
            <label htmlFor="signup-password">Password</label>
            <input
              id="signup-password"
              name="passwordOne"
              value={passwordOne}
              onChange={this.onChange}
              type="password"
              placeholder="Create a password"
              required
            />
          </div>
          <div className="auth-field">
            <label htmlFor="signup-password-confirm">Confirm password</label>
            <input
              id="signup-password-confirm"
              name="passwordTwo"
              value={passwordTwo}
              onChange={this.onChange}
              type="password"
              placeholder="Re-enter password"
              required
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="signup-phone">Phone number <span className="auth-field-note">Optional</span></label>
          <input
            id="signup-phone"
            name="phoneNumber"
            value={phoneNumber}
            onChange={this.onChange}
            type="tel"
            placeholder="Phone number"
          />
        </div>

        <div className="auth-field">
          <label htmlFor="signup-company">Company <span className="auth-field-note">Optional</span></label>
          <input
            id="signup-company"
            name="company"
            value={company}
            onChange={this.onChange}
            type="text"
            placeholder="Company"
          />
        </div>

        <div className="auth-field">
          <label htmlFor="signup-role">Role <span className="auth-field-note">Optional</span></label>
          <input
            id="signup-role"
            name="role"
            value={role}
            onChange={this.onChange}
            type="text"
            placeholder="Role"
          />
        </div>

        <label className="auth-terms-agree" htmlFor="signup-terms">
          <input
            id="signup-terms"
            name="termsAccepted"
            type="checkbox"
            checked={termsAccepted}
            onChange={this.onToggleTerms}
            className="checkbox-premium"
          />
          <span>
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="auth-link auth-link--inline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="auth-link auth-link--inline">
              Privacy Policy
            </a>
            .
          </span>
        </label>

        {error && <p className="auth-error">{error.message}</p>}

        <button
          disabled={isInvalid || isSubmitting}
          type="submit"
          className={`auth-submit ${isSubmitting ? 'auth-submit--loading' : ''}`}
        >
          {isSubmitting ? (
            <>
              <span className="auth-submit__spinner" aria-hidden="true" />
              <span>Sending verification email...</span>
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>
    );
  }
}

const SignUpLink = () => (
  <p className="auth-inline-link">
    Don't have an account?{' '}
    <Link to={ROUTES.SIGN_UP} className="auth-link">
      Sign up
    </Link>
  </p>
);

const SignUpForm = withRouter(withFirebase(SignUpFormBase));

export default SignUpPage;

export { SignUpForm, SignUpLink };
